import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import colors from "../../Constants/colors";
import theme from "../../Constants/theme";
import { Features } from "../../Enums/companyStatus";
import { Certification } from "../../Models/certification";
import { Expert } from "../../Models/expert";
import { useCertifications } from "../../Query/useCertifications";
import { useWorkspace, useWorkspaces } from "../../Query/useWorkspace";
import { useCreateUser } from "../../Query/useUsers";
import { useTranslation } from "react-i18next";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import FeaturesComponent from "./FeaturesComponent";
import WorkspaceSelector from "./WorkspaceSelector";
import UserExpertSelection from "./UserExpertSelection";
import ModelAccessSelector from "../Common/ModelAccessSelector";
import ComoAgentSelector from "../ComoAgents/ComoAgentSelector";
import { ComoInput } from "../Common/ComoInput";
import { ComoButton } from "../Common/ComoButton";

const CreateUser = () => {
  const { data: certCategoriesData } = useCertifications();
  const { t } = useTranslation("createUser");
  const { data: workspacesData } = useWorkspaces(1, 50);
  const createUserMutation = useCreateUser();
  const { colors: workspace } = useWorkspaceColors();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [user, setUser] = useState({
    name: "",
    email: "",
    password: "",
    surname: "",
    evaluation_limit: 1,
    max_company_limit: 1,
    model_ids: [] as number[],
    storage_limit: 0,
    library_limit: 0,
    cert_categories: [] as Certification[],
    is_sso: false,
    workspace_id: "" as string | number,
    features: [] as number[],
    show_locked_features: false,
  });

  const [workspaceId, setWorkspaceId] = useState<string | number>("");
  const { data: workspaceResponse } = useWorkspace(
    workspaceId ? String(workspaceId) : null,
  );

  useEffect(() => {
    if (!workspaceResponse) return;

    const ws = workspaceResponse.workspace;

    setUser((prev) => ({
      ...prev,
      evaluation_limit: ws.user_evaluation_limit ?? 0,
      max_company_limit: ws.user_company_limit ?? 0,
      storage_limit: ws.user_storage_limit ?? 0,
      library_limit: ws.user_library_limit ?? 0,
      model_ids: ws.accessible_model_ids ?? [],
      features:
        workspaceResponse.permissions?.map((p) => p.feature.id) ?? [],
      show_locked_features: ws.show_locked_features ?? false,
      cert_categories: [],
      is_sso: false,
      workspace_id: ws.id,
    }));
  }, [workspaceResponse]);

  const [selectedExperts, setSelectedExperts] = useState<Expert[]>([]);
  const [selectedComoAgentIds, setSelectedComoAgentIds] = useState<number[]>(
    [],
  );
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [, setIsChanged] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;

    if (name === "workspace_id" && !value) {
      setUser((prevUser) => ({
        ...prevUser,
        [name]: workspacesData?.workspaces?.length
          ? workspacesData.workspaces[0].id
          : "",
      }));
      setSelectedExperts([]);
      return;
    }

    setUser({ ...user, [name]: value });
  };

  const handleCertCategoriesChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    category: Certification,
  ) => {
    const isChecked = event.target.checked;

    setUser((prevUser) => {
      const updatedCategories = isChecked
        ? [...prevUser.cert_categories, category]
        : prevUser.cert_categories.filter((c) => c.id !== category.id);

      return {
        ...prevUser,
        cert_categories: updatedCategories,
      };
    });
  };

  const handleSubmit = async () => {
    if (!emailRegex.test(user.email)) {
      enqueueSnackbar(t("invalidEmail"), {
        variant: "warning",
      });
      return;
    }

    if (user.name.length < 2) {
      enqueueSnackbar(t("nameMinLength"), {
        variant: "warning",
      });
      return;
    }

    if (user.surname.length < 2) {
      enqueueSnackbar(t("surnameMinLength"), {
        variant: "warning",
      });
      return;
    }

    if (!user.is_sso && user.password.length < 7) {
      enqueueSnackbar(t("passwordMinLength"), {
        variant: "warning",
      });
      return;
    }

    const workspaceIdToSend: number | null =
      workspaceId === "" || isNaN(Number(workspaceId))
        ? null
        : Number(workspaceId);
    const cert_category_ids = user.cert_categories.map((cert) => cert.id);

    createUserMutation.mutate(
      {
        name: user.name,
        surname: user.surname,
        password: user.is_sso ? null : user.password,
        email: user.email,
        evaluationLimit: user.evaluation_limit,
        maxCompanies: user.max_company_limit,
        storageLimit: user.storage_limit,
        libraryLimit: user.library_limit,
        modelIds: user.model_ids,
        certCategoryIds: cert_category_ids,
        isSso: user.is_sso,
        workspaceId: workspaceIdToSend,
        features: user.features,
        experts: [],
        show_locked_features: user.show_locked_features,
        agent_ids: selectedComoAgentIds,
      },
      {
        onSuccess: () => {
          navigate("/users");
        },
      },
    );
  };

  const isInvalid =
    !user.name ||
    !user.surname ||
    !user.evaluation_limit ||
    !user.email ||
    !user.max_company_limit ||
    (!user.is_sso && !user.password);

  const sectionTitleSx = {
    color: workspace.primary_dark,
    fontWeight: 600,
    fontSize: "0.85rem",
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
    borderBottom: `1px solid ${colors.gray200}`,
    pb: 0.5,
    mb: 2,
  };

  const showCertSection =
    user.features.includes(Features.QUESTION_LISTS) ||
    user.features.includes(Features.EVALUATION);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: colors.gray50 }}>
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <IconButton
                onClick={() => navigate("/users")}
                size="small"
                sx={{ color: workspace.primary_dark }}
              >
                <ArrowBackIcon />
              </IconButton>
              <PersonAddOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("title")}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: colors.white,
          }}
        >
          <Typography sx={sectionTitleSx}>{t("basicInformation")}</Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ComoInput
                label={t("name")}
                name="name"
                value={user.name}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ComoInput
                label={t("surname")}
                name="surname"
                value={user.surname}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <ComoInput
                label={t("email")}
                name="email"
                value={user.email}
                onChange={(e) => {
                  const email = e.target.value.toLowerCase();
                  setUser((prevUser) => ({ ...prevUser, email }));
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={user.is_sso}
                    onChange={(e) =>
                      setUser({
                        ...user,
                        is_sso: e.target.checked,
                        password: "",
                      })
                    }
                    sx={{
                      color: colors.gray400,
                      "&.Mui-checked": { color: workspace.accent },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      color: colors.primary_dark,
                      fontSize: "0.8rem",
                    }}
                  >
                    {t("useSSO")}
                  </Typography>
                }
              />
            </Grid>

            {!user.is_sso && (
              <Grid item xs={12}>
                <ComoInput
                  label={t("password")}
                  name="password"
                  value={user.password}
                  onChange={handleChange}
                  type={showPassword ? "text" : "password"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          size="small"
                          title={showPassword ? t("hide") : t("show")}
                          sx={{ color: colors.gray500 }}
                        >
                          {showPassword ? (
                            <VisibilityIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <VisibilityOffIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
          </Grid>
          <Box sx={{ mt: 4 }}>
            <Typography sx={sectionTitleSx}>{t("workspace")}</Typography>
            <WorkspaceSelector
              workspaces={workspacesData?.workspaces || []}
              workspaceId={workspaceId}
              setWorkspaceId={setWorkspaceId as any}
            />
          </Box>
          <Box sx={{ mt: 4 }}>
            <Typography sx={sectionTitleSx}>{t("limits")}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <ComoInput
                  label={t("evaluationLimit")}
                  name="evaluation_limit"
                  value={user.evaluation_limit}
                  onChange={handleChange}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ComoInput
                  label={t("maxCompanyLimit")}
                  name="max_company_limit"
                  value={user.max_company_limit}
                  onChange={handleChange}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ComoInput
                  label={t("storageLimit")}
                  name="storage_limit"
                  value={user.storage_limit}
                  onChange={handleChange}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ComoInput
                  label={t("libraryLimit")}
                  name="library_limit"
                  value={user.library_limit}
                  onChange={handleChange}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 4 }}>
            <ModelAccessSelector
              selectedModelIds={user.model_ids}
              onChange={(modelIds) =>
                setUser((prev) => ({ ...prev, model_ids: modelIds }))
              }
              isMobile={isMobile}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FeaturesComponent
              selectedFeatures={user.features}
              setSelectedFeatures={(features) => {
                setUser((prevUser) => ({
                  ...prevUser,
                  features:
                    typeof features === "function"
                      ? features(prevUser.features)
                      : features,
                }));
              }}
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={user.show_locked_features}
                  onChange={(e) =>
                    setUser({
                      ...user,
                      show_locked_features: e.target.checked,
                    })
                  }
                  sx={{
                    color: colors.gray400,
                    "&.Mui-checked": { color: workspace.accent },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    color: colors.primary_dark,
                    fontSize: "0.875rem",
                  }}
                >
                  {t("showLockedFeatures")}
                </Typography>
              }
            />
          </Box>

          {false && (
            <UserExpertSelection
              workspaceId={workspaceId ? Number(workspaceId) : undefined}
              selectedExperts={selectedExperts}
              setSelectedExperts={setSelectedExperts}
              setIsChanged={setIsChanged}
              isEditable={true}
              isEditPage={false}
            />
          )}

          {user.features.includes(Features.COMO_EXPERTS) && (
            <ComoAgentSelector
              selectedAgentIds={selectedComoAgentIds}
              onChange={setSelectedComoAgentIds}
            />
          )}

          {showCertSection && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={sectionTitleSx}>
                {t("chooseCertCategories")}
              </Typography>
              <Grid container spacing={1}>
                {certCategoriesData?.map((category) => (
                  <Grid item xs={12} sm={6} md={4} key={category.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={user.cert_categories.some(
                            (c) => c.id === category.id,
                          )}
                          onChange={(event) =>
                            handleCertCategoriesChange(event, category)
                          }
                          sx={{
                            color: colors.gray400,
                            "&.Mui-checked": { color: workspace.accent },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            color: colors.primary_dark,
                            fontSize: "0.875rem",
                          }}
                        >
                          {category.name}
                        </Typography>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: 4 }}>
            <Tooltip
              title={t("fillAllFields")}
              disableHoverListener={!isInvalid}
            >
              <span>
                <ComoButton
                  onClick={handleSubmit}
                  disabled={isInvalid}
                  fullWidth
                  sx={{
                    py: 1.25,
                    fontSize: "0.95rem",
                    fontWeight: 600,
                  }}
                >
                  {t("create")}
                </ComoButton>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CreateUser;
